
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaFormField from "@/components/ui/mediaFormField";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/ui/RichTextEditor";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  content: z.string().min(1, "Content is required"),
  media_url: z.string().optional(),
  //thumbnail_url: z.string().optional(),
  type: z.enum(["reel", "video", "post"], { errorMap: () => ({ message: "Type is required" }) }),
  category_id: z.coerce.number().min(1, "Category is required"),
  subcategory: z.string().optional(),
  tags: z.string().optional(),
  is_featured: z.boolean().optional(),
  author: z.string().optional(),
  source: z.string().optional(),
});

interface FeedEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: any;
  setForm: (updater: (prev: any) => any) => void;
  onSave: (data: any) => Promise<void> | void; // receives latest merged form data
}

export default function FeedEditDialog({ open, onOpenChange, form, setForm, onSave }: FeedEditDialogProps) {
  const [media, setMedia] = React.useState<File | null>(null);
  const [mediaUrl, setMediaUrl] = React.useState(form.media_url || "");
  const [categories, setCategories] = React.useState<{ id: number; name: string }[]>([]);
  const [loadingCategories, setLoadingCategories] = React.useState(true);
  const rhf = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: form,
    values: form,
  });

  React.useEffect(() => {
    rhf.reset(form);
    setMediaUrl(form.media_url || "");
  }, [form, open]);

  React.useEffect(() => {
  async function fetchCategories() {
      try {
    const res = await (await import("@/api/axios")).axiosPrivate.get("/feeds/categories");
        setCategories(res.data.data || []);
      } catch (err) {
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    }
    fetchCategories();
  }, []);

  const [loading, setLoading] = React.useState(false);

  async function handleSubmit(data: z.infer<typeof formSchema>) {
    setLoading(true);
    let finalMediaUrl = mediaUrl;
    if (media) {
      try {
        finalMediaUrl = await (await import("@/lib/firebase/mediaUploading")).uploadFileToFirebase(media, "posts_media");
        setMediaUrl(finalMediaUrl);
      } catch (err) {
        alert("Failed to upload media to Firebase");
        setLoading(false);
        return;
      }
    }
    const merged = { ...form, ...data, media_url: finalMediaUrl };
    setForm(() => merged); // sync local state for reopening without refetch
    await onSave(merged);
    setLoading(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full !max-w-[98vw] h-[95vh] flex flex-col p-8">
        <DialogHeader>
          <DialogTitle>Edit Feed</DialogTitle>
          <DialogDescription>Update feed details below and click Save Changes.</DialogDescription>
        </DialogHeader>
        <Form {...rhf}>
          <form className="space-y-6 flex-1 overflow-y-auto" onSubmit={rhf.handleSubmit(handleSubmit)}>
            {/* Top row: Type, Category, Sub-Category */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={rhf.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="post">Post</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="reel">Reel</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value && field.value > 0 ? String(field.value) : ""}
                        onValueChange={val => field.onChange(Number(val))}
                        disabled={loadingCategories}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={loadingCategories ? "Loading..." : "Select Category"} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.length === 0 && !loadingCategories && (
                            <SelectItem value="no-categories" disabled>No categories available</SelectItem>
                          )}
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* <FormField
                control={rhf.control}
                name="subcategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sub Category</FormLabel>
                    <FormControl>
                      <Select value={field?.value || ""} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Sub Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subcategory1">Sub Category 1</SelectItem>
                          <SelectItem value="subcategory2">Sub Category 2</SelectItem>
                          <SelectItem value="subcategory3">Sub Category 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
            </div>
            {/* Second row: Title and Description */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={rhf.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Enter description"
                        minHeight="250px"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Enter content"
                        minHeight="250px"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={rhf.control}
                name="author"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author</FormLabel>
                    <FormControl>
                      <Input placeholder="Author name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder="Source (e.g. original link)" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags (comma separated)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* <FormField
                control={rhf.control}
                name="thumbnail_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Thumbnail URL</FormLabel>
                    <FormControl>
                      <Input placeholder="Thumbnail URL" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <FormField
                control={rhf.control}
                name="is_featured"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="mb-0">Featured</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="space-y-3">
              <Label>Image / Video</Label>
              <MediaFormField
                media={media}
                setMedia={setMedia}
                setMediaUrl={setMediaUrl}
                refPath="posts_media"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
